/**
 * Storage and limits (storage accounting, quota management, platform ceilings).
 *
 * - `GET /admin/v1/organisation/storage`: what an organisation keeps, store by store, against its
 *   limits and the retention that bounds each. Counted hourly by the `storage.account` job into one
 *   row per organisation, so the read is cheap.
 * - `GET/PUT /admin/v1/quotas`: an Enterprise organisation's own limits (no billing): a default for
 *   the whole organisation and overrides for a person or a project.
 * - `GET /operator/v1/capacity`: the platform's own ceilings, for Mojo Up's operators.
 */
import { z } from "zod";
/** The stores an organisation's bytes are kept in. */
export const STORAGE_STORE_KEYS = ["backups", "assets", "evidence", "exports", "invoices", "memory", "audit", "flows", "usage", "runs", "projects", "teams", "catalogue", "inbox", "organisation"];
/** The stores weighed table by table (AIOE 0.60.0): every other organisation table, grouped by what it is. */
export const STORAGE_DATA_GROUPS = ["projects", "teams", "catalogue", "inbox", "organisation"];
export const storageStoreKeySchema = z.enum(STORAGE_STORE_KEYS);
/** Rows of the logs are counted, not weighed: each is charged at a fixed size so the total moves with them. */
export const STORAGE_ROW_BYTES = { audit: 1024, flows: 256, usage: 384, runs: 512, invoices: 4096 };
export const STORAGE_STORE_TEXT = {
    backups: { label: "Backups", what: "People's encrypted workbench backups", unit: "bytes" },
    assets: { label: "Design system files", what: "Fonts, logos and stylesheets of the design systems", unit: "bytes" },
    evidence: { label: "Assessment evidence", what: "Documents uploaded as evidence for provider assessments", unit: "bytes" },
    exports: { label: "Exports", what: "Organisation export archives waiting to be downloaded", unit: "bytes" },
    invoices: { label: "Invoices", what: "Provider invoices uploaded to reconcile spend", unit: "bytes" },
    memory: { label: "Memory", what: "Project memory items", unit: "bytes" },
    audit: { label: "Audit trail", what: "The tamper-evident record of what happened", unit: "bytes" },
    flows: { label: "Connection logs", what: "Mesh connection records", unit: "bytes" },
    usage: { label: "Usage records", what: "Raw model-call records (the daily rollups stay)", unit: "bytes" },
    runs: { label: "Agent run records", what: "The run records kept for the activity graph", unit: "bytes" },
    projects: { label: "Projects", what: "Boards, plans, conversations, documents, maps, risks, incidents and pull requests", unit: "bytes" },
    teams: { label: "Teams", what: "Teams and specialists, their evaluations and agent identities", unit: "bytes" },
    catalogue: { label: "Catalogue", what: "Skills, roles, prompts, tool servers and design system records", unit: "bytes" },
    inbox: { label: "Inbox", what: "Inbox records and phone notification tokens", unit: "bytes" },
    organisation: { label: "Organisation", what: "People, machines, policies, secrets, budgets and everything else the organisation keeps", unit: "bytes" },
};
/** What one store holds as the hourly job counted it. */
export const storageFigureSchema = z.object({
    key: storageStoreKeySchema,
    /** Items (objects, rows, memory items) when it has a count. */
    count: z.number().int().min(0).optional(),
    bytes: z.number().int().min(0),
    /** True when the bytes are rows at a fixed size, not weighed. */
    estimated: z.boolean(),
});
/** One store as `GET /admin/v1/organisation/storage` answers it. */
export const storageRowSchema = storageFigureSchema.extend({
    label: z.string(),
    what: z.string(),
    /** What bounds how long it is kept, in words. */
    retention: z.string(),
    /** The limit that applies to this store alone, in its unit (bytes, or items for memory); null is none. */
    limit: z.number().int().min(0).nullable(),
    /** The unit `limit` is in: bytes, or items (memory). */
    limitUnit: z.enum(["bytes", "count"]).optional(),
    /** Words for the limit when it is not a plain total (per person, per export, items). */
    limitText: z.string().optional(),
    /** Set at 80 % and at the limit: words that say so. */
    atLimit: z.string().optional(),
    state: z.enum(["ok", "near", "full"]),
});
export const organisationStorageSchema = z.object({
    organisationId: z.string(),
    /** free | teams on Cloud; enterprise (limits are the administrators' own) otherwise. */
    plan: z.enum(["free", "teams", "enterprise"]),
    /** When the hourly job last counted; absent until it has. */
    measuredAt: z.string().optional(),
    stores: z.array(storageRowSchema),
    total: z.object({
        bytes: z.number().int().min(0),
        limit: z.number().int().min(0).nullable(),
        state: z.enum(["ok", "near", "full"]),
        atLimit: z.string().optional(),
    }),
    /** Whose limits these are: the plan's (Cloud) or the administrators' (Enterprise, Quotas). */
    limitsFrom: z.enum(["plan", "administrators"]),
    /** Machines on the organisation's mesh, counted with the rest. */
    meshNodes: z.number().int().min(0),
    /**
     * AIOE 0.60.0, Enterprise on Postgres: the whole database's size on disk now, against the disk the deployment gave it
     * (`allocatedBytes`, from the deployment files; null when not known). Deployment-wide, not this organisation's share.
     */
    database: z
        .object({ bytes: z.number().int().min(0), allocatedBytes: z.number().int().min(0).nullable(), state: z.enum(["ok", "near", "full"]), atLimit: z.string().optional() })
        .optional(),
});
// ── Quota policy (Enterprise, no billing) ─────────────────────────────────────
/** The limits an administrator sets. Whole numbers (bytes, or counts), null (or absent) for none. */
export const QUOTA_POLICY_KEYS = ["storageBytes", "backupBytes", "assetBytes", "meshNodes", "exportBytes"];
export const quotaPolicyKeySchema = z.enum(QUOTA_POLICY_KEYS);
export const QUOTA_POLICY_TEXT = {
    storageBytes: { label: "Total storage", unit: "bytes", what: "Everything the organisation keeps" },
    backupBytes: { label: "Backups", unit: "bytes", what: "Each person's encrypted backups" },
    assetBytes: { label: "Design system files", unit: "bytes", what: "Fonts, logos and stylesheets of the design systems, all together" },
    meshNodes: { label: "Machines on the mesh", unit: "count", what: "Workbenches and nodes on the private network" },
    exportBytes: { label: "Largest export", unit: "bytes", what: "The most one organisation export may hold" },
};
/** Which limits a person's or a project's own figure may replace. */
export const QUOTA_SUBJECT_KEYS = { user: ["backupBytes", "exportBytes"], project: [] };
const limitSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable();
export const quotaLimitsSchema = z.object({
    storageBytes: limitSchema.optional(),
    backupBytes: limitSchema.optional(),
    assetBytes: limitSchema.optional(),
    meshNodes: limitSchema.optional(),
    exportBytes: limitSchema.optional(),
    /** Retired (2 Nov 2026): memory is limited by storage bytes, never by item count. Read so stored policies parse; ignored, and dropped when a policy is saved. */
    memoryItems: z.unknown().optional(),
}).strict();
export const quotaOverrideSchema = z.object({
    subjectType: z.enum(["user", "project"]),
    subjectId: z.string().min(1).max(200),
    /** A name to show (the person's or the project's); the id is what counts. */
    name: z.string().max(200).optional(),
    limits: quotaLimitsSchema,
});
/** `PUT /admin/v1/quotas` takes this whole; `GET` answers it with the usage beside it. */
export const quotaPolicySchema = z.object({
    defaults: quotaLimitsSchema,
    overrides: z.array(quotaOverrideSchema).max(500),
});
export const quotaPolicyViewSchema = quotaPolicySchema.extend({
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
    /** What the organisation uses now (the hourly count), by limit, beside its default. */
    used: z.object({
        storageBytes: z.number().int().min(0),
        backupBytes: z.number().int().min(0),
        assetBytes: z.number().int().min(0),
        memoryBytes: z.number().int().min(0),
        meshNodes: z.number().int().min(0),
        largestExportBytes: z.number().int().min(0),
    }),
    /** Per override: what that person or project uses of each limit it sets. */
    overrideUsed: z.array(z.object({ subjectType: z.enum(["user", "project"]), subjectId: z.string(), used: z.record(z.string(), z.number().int().min(0)) })),
    /** Edition: on Cloud the plan's limits apply and this is read only. */
    editable: z.boolean(),
});
// ── Platform ceilings ─────────────────────────────────────────────────────────
/** `AIOE_PLATFORM_CEILINGS` (JSON): bounds on the whole deployment, so its own bill is bounded. Absent or null is none. */
export const platformCeilingsSchema = z
    .object({
    /** All organisations' stored bytes together. */
    storageBytes: z.number().int().min(0).nullable().optional(),
    /** Organisations made in one day. */
    organisationsPerDay: z.number().int().min(0).nullable().optional(),
    /** Machines on every mesh together. */
    meshNodes: z.number().int().min(0).nullable().optional(),
})
    .strict();
export const capacityLineSchema = z.object({
    key: z.enum(["storageBytes", "organisationsPerDay", "meshNodes"]),
    label: z.string(),
    used: z.number().int().min(0),
    ceiling: z.number().int().min(0).nullable(),
    state: z.enum(["ok", "near", "crossed"]),
});
/** `GET /operator/v1/capacity`. */
export const capacitySchema = z.object({
    /** When the organisations' figures were last counted (the oldest of them), if any were. */
    measuredAt: z.string().optional(),
    /** Whether any ceiling is set (AIOE_PLATFORM_CEILINGS). */
    configured: z.boolean(),
    organisations: z.number().int().min(0),
    lines: z.array(capacityLineSchema),
    /** True while any ceiling is crossed: new organisations and growth on the Free plan are refused. */
    refusing: z.boolean(),
    /** The open status incident the job made, when there is one. */
    incidentId: z.string().optional(),
    /** Egress is not measured here: the platform's network meter (Azure) is where that bill is read. */
    egressMeasured: z.literal(false),
    /** The ten organisations holding most, for finding what grew. */
    largest: z.array(z.object({ organisationId: z.string(), name: z.string().optional(), bytes: z.number().int().min(0) })),
});
/** The refusal body when a platform ceiling stops growth: 503 `capacity`. */
export const capacityRefusalSchema = z.object({ error: z.literal("capacity"), ceiling: z.enum(["storageBytes", "organisationsPerDay", "meshNodes"]), message: z.string() });
//# sourceMappingURL=storage.js.map